import { useCallback, useState } from "react";

import { useHistory } from "react-router-dom";
import { type CloudAnalyticsModelMetricModel, type Collaborators, type PublicAccess } from "@doitintl/cmp-models";
import { modelServerTimestamp, type ModelTimestamp } from "@doitintl/models-any";
import { Typography } from "@mui/material";

import { useApiContext } from "../../../api/context";
import { metricText } from "../../../assets/texts";
import { metricTxt } from "../../../assets/texts/CloudAnalytics";
import ResourceEditDialog from "../../../Components/EditableResourceHeader/ResourceEditDialog";
import { useCustomerId } from "../../../Components/hooks/useCustomerId";
import useRouteMatchURL from "../../../Components/hooks/useRouteMatchURL";
import {
  useErrorSnackbar,
  useInfoSnackbar,
  useSnackbar,
  useSuccessSnackbar,
} from "../../../Components/SharedSnackbar/SharedSnackbar.context";
import { ThreeDotsMenu } from "../../../Components/ThreeDotsMenu";
import { useAuthContext } from "../../../Context/AuthContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useIsFeatureEntitled } from "../../../Context/TierProvider";
import { consoleErrorWithSentry } from "../../../utils";
import mixpanel from "../../../utils/mixpanel";
import { isOwner } from "../utilities";
import { shareMetrics } from "./api";
import { copyMetric, saveMetricData } from "./db";
import { MetricsMenuOptions } from "./types";
import type { MetricWSnap } from "../../../types";

export const useCreateMetricHandler = ({
  mixpanelEventName,
  baseUrl,
}: {
  mixpanelEventName: string;
  baseUrl: string;
}) => {
  const history = useHistory();
  const routeMatchURL = useRouteMatchURL();

  return useCallback(() => {
    mixpanel.track(mixpanelEventName);
    history.push(`${baseUrl}/create`, { prevPage: routeMatchURL });
  }, [baseUrl, history, mixpanelEventName, routeMatchURL]);
};

export const useDuplicateMetric = (row: MetricWSnap) => {
  const infoSnackbar = useInfoSnackbar(3);
  const { customer } = useCustomerContext();
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const errorSnackbar = useErrorSnackbar();

  return useCallback(async () => {
    try {
      await copyMetric(customer.ref, currentUser.email, row.data);
      infoSnackbar(metricTxt.DUPLICATE_SUCCESSFUL);
    } catch (e) {
      errorSnackbar(metricTxt.CREATE_METRIC_ERROR);
      consoleErrorWithSentry(e);
    }
  }, [currentUser.email, customer.ref, errorSnackbar, infoSnackbar, row.data]);
};

export const useRenameMetric = (row: MetricWSnap) => {
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const successSnackbar = useSuccessSnackbar();
  const errorSnackbar = useErrorSnackbar();

  const handleSave = useCallback(
    async (values: { name: string; description?: string }) => {
      try {
        const metricData: Partial<CloudAnalyticsModelMetricModel> = {
          name: values.name,
          description: values.description ?? row.data.description,
          timeModified: modelServerTimestamp() as ModelTimestamp,
        };
        setIsRenameDialogOpen(false);
        await saveMetricData({ ...row.data, ...metricData }, row.ref.id);
        successSnackbar(metricTxt.METRIC_SAVE_SUCCESS);
      } catch (e) {
        errorSnackbar(metricTxt.METRIC_SAVE_ERROR);
        consoleErrorWithSentry(e);
      }
    },
    [row, errorSnackbar, successSnackbar]
  );

  return {
    isRenameDialogOpen,
    openRenameDialog: () => {
      setIsRenameDialogOpen(true);
    },
    closeRenameDialog: () => {
      setIsRenameDialogOpen(false);
    },
    handleSave,
  };
};

export const useThreeDotsMetricsMenu = (row: MetricWSnap, onEditPermission: (row: MetricWSnap) => void) => {
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const isEntitledCalculatedMetrics = useIsFeatureEntitled("analytics:calculatedMetrics");
  const duplicateMetric = useDuplicateMetric(row);
  const { openRenameDialog, isRenameDialogOpen, closeRenameDialog, handleSave } = useRenameMetric(row);

  return (
    <>
      <ThreeDotsMenu
        closeAfterSelect
        options={[
          {
            label: <Typography>{MetricsMenuOptions.DUPLICATE}</Typography>,
            action: duplicateMetric,
            key: MetricsMenuOptions.DUPLICATE,
          },
          ...(isOwner(currentUser.email, row.data)
            ? [
                {
                  label: <Typography>{MetricsMenuOptions.EDIT_PERMISSIONS}</Typography>,
                  action: () => {
                    onEditPermission(row);
                  },
                  key: MetricsMenuOptions.EDIT_PERMISSIONS,
                },
                {
                  label: <Typography>{MetricsMenuOptions.RENAME}</Typography>,
                  action: openRenameDialog,
                  key: MetricsMenuOptions.RENAME,
                },
              ]
            : []),
        ]}
        disabled={!isEntitledCalculatedMetrics}
      />
      {isRenameDialogOpen && (
        <ResourceEditDialog
          open={isRenameDialogOpen}
          onClose={closeRenameDialog}
          onSave={handleSave}
          initialValues={{
            name: row.data.name,
            description: row.data.description,
          }}
          resourceName="metric"
        />
      )}
    </>
  );
};

export const useShareMetricHandler = (
  metrics: MetricWSnap[],
  onSharingCompleted: () => void,
  onUpdateLoading: (loading: boolean) => void
) => {
  const api = useApiContext();
  const customerId = useCustomerId();
  const snackbar = useSnackbar();

  return async (collaborators: Collaborators, publicAccess: PublicAccess) => {
    try {
      onUpdateLoading(true);
      await shareMetrics(api, customerId, metrics, collaborators, publicAccess);

      mixpanel.track("analytics.metrics.share", {
        metricIds: metrics.map((m) => m.ref.id).join(","),
        role: publicAccess,
      });

      snackbar.onOpen({
        message: metricText.SHARE_METRIC_SUCCESS,
        variant: "success",
        autoHideDuration: 5000,
      });

      onSharingCompleted();
    } catch (e) {
      snackbar.onOpen({
        message: metricText.SHARE_METRICS_FAILURE,
        variant: "error",
        autoHideDuration: 5000,
      });
      consoleErrorWithSentry(e);
    } finally {
      onUpdateLoading(false);
    }
  };
};
